import { zodResolver } from "@hookform/resolvers/zod";
import {
  LoginErrorState,
  LoginView,
} from "dsl/ecosystems/CareproviderOnboardingLoginPage/useHandleLogin";
import { useCallback, useEffect, useRef } from "react";
import { DefaultValues, Path, useForm, UseFormReturn } from "react-hook-form";
import { useTranslations } from "translations";
import Translations from "translations/types";
import { getLoginDefaultValues } from "./getLoginDefaultValues";
import {
  getLoginSchema,
  LoginFormValues,
  LoginParsedValues,
} from "./loginSchema";
import { useLoginFieldSideEffects } from "./useLoginFieldSideEffects";

const LOGIN_FIELD_NAMES = ["email", "password", "challenge"] as const;

type ResolverCtx = {
  isSingleSignOn: boolean;
  translations: Translations;
  view: LoginView;
};

type UseLoginFormParams = {
  isSingleSignOn?: boolean;
  loginErrors?: LoginErrorState;
  loginView: LoginView;
  onBackToLogin: () => void;
  onChangeEmail?: (value: string) => void;
  onSubmit: (payload: LoginParsedValues) => void;
  resetError: () => void;
  ssoToken?: string;
};

function useSyncLoginExternalErrors({
  loginErrors,
  methods,
}: {
  loginErrors?: LoginErrorState;
  methods: UseFormReturn<LoginFormValues, unknown, LoginParsedValues>;
}) {
  useEffect(() => {
    LOGIN_FIELD_NAMES.forEach((field) => {
      const fieldName = field as Path<LoginFormValues>;
      const externalMessage = loginErrors?.[field];
      if (externalMessage) {
        methods.setError(fieldName, {
          message: externalMessage,
          type: "manual",
        });
      } else if (methods.getFieldState(fieldName).error?.type === "manual") {
        methods.clearErrors(fieldName);
      }
    });
  }, [loginErrors, methods]);
}

export function useLoginForm({
  isSingleSignOn = false,
  loginErrors,
  loginView,
  onBackToLogin,
  onChangeEmail,
  onSubmit,
  resetError,
  ssoToken,
}: UseLoginFormParams) {
  const translations = useTranslations();

  const resolverCtxRef = useRef<ResolverCtx>({
    isSingleSignOn,
    translations,
    view: loginView,
  });
  resolverCtxRef.current = {
    isSingleSignOn,
    translations,
    view: loginView,
  };

  const getResetValues = useCallback(
    (): DefaultValues<LoginFormValues> => getLoginDefaultValues({ ssoToken }),
    [ssoToken],
  );

  const methods = useForm<LoginFormValues, unknown, LoginParsedValues>({
    defaultValues: getLoginDefaultValues({ ssoToken }),
    resolver: async (values, context, options) => {
      const schema = getLoginSchema(resolverCtxRef.current);
      return zodResolver(schema)(values, context, options);
    },
  });

  useEffect(() => {
    methods.setValue("ssoToken", ssoToken, { shouldDirty: false });
  }, [methods, ssoToken]);

  useLoginFieldSideEffects({
    onChangeEmail,
    resetError,
    watch: methods.watch,
  });

  useSyncLoginExternalErrors({ loginErrors, methods });

  const handleValid = useCallback(
    (values: LoginParsedValues) => {
      onSubmit(values);
    },
    [onSubmit],
  );

  const handleBackToLogin = useCallback(() => {
    methods.reset(getResetValues());
    onBackToLogin();
  }, [getResetValues, methods, onBackToLogin]);

  return {
    handleBackToLogin,
    handleSubmit: methods.handleSubmit(handleValid),
    methods,
  };
}
